import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { Plus, ArrowLeft, Pencil, Trash2, ListChecks } from "lucide-react";
import { useTranslation } from "../../contexts/I18nContext";
import { handleApiError } from "../../utils/errorHandler";
import { usePermissions } from "../../hooks/usePermissions";

const EMPTY_FORM = { codice: "", nome: "", descrizione: "" };

export default function ProduzioneTipiAttivitaPage() {
  const { t } = useTranslation();
  const { tenant } = useParams();
  const navigate = useNavigate();
  const { canEdit } = usePermissions();
  const canEditProduzione = canEdit("produzione");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/tenant/produzione/tipi-attivita");
      setItems(res.data);
    } catch (e) {
      handleApiError(e, t("errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setShowDialog(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ codice: it.codice || "", nome: it.nome || "", descrizione: it.descrizione || "" });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.codice.trim() || !form.nome.trim()) {
      toast.error("Codice e nome obbligatori");
      return;
    }
    try {
      if (editing) {
        await api.put(`/tenant/produzione/tipi-attivita/${editing.id}`, form);
        toast.success("Tipo attività aggiornato");
      } else {
        await api.post("/tenant/produzione/tipi-attivita", form);
        toast.success("Tipo attività creato");
      }
      setShowDialog(false);
      load();
    } catch (e) {
      handleApiError(e, t("errors.saveFailed"));
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Eliminare il tipo attività? Sarà rimosso dalle sottocommesse collegate.")) return;
    try {
      await api.delete(`/tenant/produzione/tipi-attivita/${id}`);
      toast.success("Tipo attività eliminato");
      load();
    } catch (e) {
      handleApiError(e, t("errors.deleteFailed"));
    }
  };

  return (
    <div className="space-y-6" data-testid="produzione-tipi-attivita-page">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/${tenant}/produzione`)} data-testid="btn-back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ListChecks className="h-6 w-6" />
              Tipologie Attività
            </h1>
            <p className="text-muted-foreground">Anagrafica tipologie di attività per suddividere le sottocommesse</p>
          </div>
        </div>
        {canEditProduzione && (
          <Button onClick={openCreate} data-testid="btn-nuovo-tipo-attivita">
            <Plus className="h-4 w-4 mr-2" />
            Nuova Tipologia
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{items.length} tipologie</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-muted-foreground py-8">Caricamento...</p>
          ) : items.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Nessuna tipologia definita</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[150px]">Codice</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Descrizione</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map(it => (
                  <TableRow key={it.id} data-testid={`tipo-attivita-row-${it.id}`}>
                    <TableCell className="font-mono font-medium">{it.codice}</TableCell>
                    <TableCell>{it.nome}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{it.descrizione || "-"}</TableCell>
                    <TableCell>
                      {canEditProduzione && (
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(it)} data-testid={`btn-edit-${it.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(it.id)} data-testid={`btn-delete-${it.id}`}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Modifica Tipologia Attività" : "Nuova Tipologia Attività"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Codice *</Label>
                <Input value={form.codice} onChange={e => setForm({ ...form, codice: e.target.value })} placeholder="es. SALD, MONT, VERN" data-testid="input-codice" />
              </div>
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} placeholder="es. Saldatura" data-testid="input-nome" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t("common.description")}</Label>
              <Textarea rows={3} value={form.descrizione} onChange={e => setForm({ ...form, descrizione: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleSave} data-testid="btn-salva-tipo-attivita">{editing ? "Aggiorna" : "Crea"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
